import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Trash2, Plus, Menu, X, Settings, User, ChevronRight,
  GripVertical, ArrowLeft, Share2, Check, AlertCircle
} from 'lucide-react';
import { saveRecentList } from './HomePage';

const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8000/api'
  : 'https://hitlist.cc/api';

const ACCENT_COLORS = [
  { name: 'Blue',   value: '#2563EB' },
  { name: 'Indigo', value: '#4F46E5' },
  { name: 'Purple', value: '#9333EA' },
  { name: 'Red',    value: '#DC2626' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Green',  value: '#16A34A' },
  { name: 'Teal',   value: '#0D9488' },
  { name: 'Pink',   value: '#DB2777' },
];

const TodoListPage = ({ listId, onNavigateHome }) => {
  const [list, setList]             = useState(null);
  const [todos, setTodos]           = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [error, setError]           = useState(null);
  const [filter, setFilter]         = useState('active');
  const [newTodo, setNewTodo]       = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile]   = useState(false);
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editingText, setEditingText]     = useState('');
  const [editingTitle, setEditingTitle]   = useState(false);
  const [titleText, setTitleText]         = useState('');
  const [copied, setCopied]               = useState(false);

  // ── Load list + todos ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [listRes, todosRes] = await Promise.all([
          fetch(`${API_BASE}/lists.php?list_id=${listId}`),
          fetch(`${API_BASE}/todos.php?list_id=${listId}`),
        ]);

        if (listRes.status === 404) {
          setError('not_found');
          setIsLoading(false);
          return;
        }
        if (!listRes.ok || !todosRes.ok) throw new Error('Server error');

        const listData  = await listRes.json();
        const todosData = await todosRes.json();

        setList(listData);
        setTitleText(listData.name);

        // Normalize todos to match the shape the rest of the component expects
        const normalized = todosData.map(t => ({
          id:        String(t.id),
          text:      t.title,
          completed: t.is_completed === 1 || t.is_completed === true || t.is_completed === '1',
          time:      t.created_at,
          order:     t.position,
          _apiId:    t.id, // integer PK for individual updates
        }));
        setTodos(normalized);

        saveRecentList(listId, listData.name);
      } catch (e) {
        setError('load_failed');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [listId]);

  const accentColor = list?.accent_color || '#2563EB';

  // ── Helpers ──────────────────────────────────────────────────────────────
  const saveListColor = async (color) => {
    setList(prev => ({ ...prev, accent_color: color }));
    await fetch(`${API_BASE}/lists.php?list_id=${listId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accent_color: color }),
    });
  };

  const saveListName = async (name) => {
    setList(prev => ({ ...prev, name }));
    saveRecentList(listId, name);
    await fetch(`${API_BASE}/lists.php?list_id=${listId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  };

  // ── Add todo ─────────────────────────────────────────────────────────────
  const handleAddTodo = async () => {
    if (!newTodo.trim()) return;
    const title = newTodo.trim();
    setNewTodo('');

    try {
      const res = await fetch(`${API_BASE}/todos.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_id: listId, title }),
      });
      const created = await res.json();
      const newItem = {
        id:       String(created.id),
        text:     created.title,
        completed: false,
        time:     created.created_at,
        order:    created.position,
        _apiId:   created.id,
      };
      setTodos(prev => [...prev, newItem]);
    } catch {
      // Optimistically added — silently fail is fine for now
    }
  };

  // ── Toggle complete ──────────────────────────────────────────────────────
  const toggleTodo = async (id) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;
    const newVal = !todo.completed;
    setTodos(prev => prev.map(t => t.id === id ? { ...t, completed: newVal } : t));
    await fetch(`${API_BASE}/todos.php?id=${todo._apiId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_completed: newVal }),
    });
  };

  // ── Delete todo ──────────────────────────────────────────────────────────
  const deleteTodo = async (id) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;
    setTodos(prev => prev.filter(t => t.id !== id));
    await fetch(`${API_BASE}/todos.php?id=${todo._apiId}`, { method: 'DELETE' });
  };

  // ── Edit todo text ───────────────────────────────────────────────────────
  const saveEdit = async (id) => {
    if (!editingText.trim()) { setEditingTodoId(null); return; }
    const todo = todos.find(t => t.id === id);
    const newText = editingText.trim();
    setTodos(prev => prev.map(t => t.id === id ? { ...t, text: newText } : t));
    setEditingTodoId(null);
    setEditingText('');
    await fetch(`${API_BASE}/todos.php?id=${todo._apiId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newText }),
    });
  };

  // ── Drag & drop ──────────────────────────────────────────────────────────
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;

    const visible = [...filteredTodos];
    const [moved] = visible.splice(src, 1);
    visible.splice(dst, 0, moved);

    // Rebuild full todos list preserving non-visible items
    const updatedTodos = todos.map(todo => {
      const newPos = visible.findIndex(v => v.id === todo.id);
      return newPos !== -1 ? { ...todo, order: newPos } : todo;
    });
    setTodos(updatedTodos);

    // Push position updates to the server individually
    const updates = visible.map((todo, idx) => ({ _apiId: todo._apiId, position: idx }));
    await Promise.all(
      updates.map(u =>
        fetch(`${API_BASE}/todos.php?id=${u._apiId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ position: u.position }),
        })
      )
    );
  };

  // ── Filtered / sorted list ───────────────────────────────────────────────
  const filteredTodos = useMemo(() =>
    todos
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter(t => {
        if (filter === 'active')    return !t.completed;
        if (filter === 'completed') return  t.completed;
        return true;
      }),
    [todos, filter]
  );

  // ── Share / copy URL ─────────────────────────────────────────────────────
  const handleShare = () => {
    const url = `${window.location.origin}/${listId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Close menu helper ────────────────────────────────────────────────────
  const closeMenu = () => { setIsMenuOpen(false); setShowSettings(false); setShowProfile(false); };

  // ── States ────────────────────────────────────────────────────────────────
  if (isLoading) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error === 'not_found') return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">List Not Found</h2>
        <p className="text-gray-500 mb-2">No list exists with the ID:</p>
        <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono">{listId}</code>
        <p className="text-gray-400 text-sm mt-2 mb-6">It may have been deleted or the ID is incorrect.</p>
        <button onClick={onNavigateHome} className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Back to Home
        </button>
      </div>
    </div>
  );

  if (error === 'load_failed') return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <AlertCircle size={48} className="text-orange-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Connection Error</h2>
        <p className="text-gray-500 mb-6">Could not connect to the server.</p>
        <button onClick={() => window.location.reload()} className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 mr-2">Retry</button>
        <button onClick={onNavigateHome} className="text-gray-500 px-4 py-3">Home</button>
      </div>
    </div>
  );

  const currentDate = new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto bg-white shadow-lg relative">

        {/* Overlay */}
        {isMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={closeMenu} />}

        {/* Side menu */}
        <div className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <span className="font-bold text-xl">
              {showSettings ? 'Settings' : showProfile ? 'Profile' : 'Menu'}
            </span>
            <button onClick={closeMenu} className="text-gray-500 hover:text-blue-600"><X size={24} /></button>
          </div>
          <div className="py-2">
            {showSettings ? (
              <div className="p-4">
                <button onClick={() => setShowSettings(false)} className="flex items-center gap-2 text-gray-600 mb-4 hover:text-gray-800">
                  <ChevronRight size={16} className="rotate-180" /> Back
                </button>
                <h3 className="font-semibold mb-3">Accent Color</h3>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {ACCENT_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => saveListColor(c.value)}
                      className={`w-10 h-10 rounded-full border-2 transition-transform ${accentColor === c.value ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                    />
                  ))}
                  <label
                    className={`w-10 h-10 rounded-full border-2 cursor-pointer flex items-center justify-center ${!ACCENT_COLORS.some(c => c.value === accentColor) ? 'border-gray-800 scale-110' : 'border-gray-300'}`}
                    style={{ background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
                    title="Custom color"
                  >
                    <input type="color" value={accentColor} onChange={e => saveListColor(e.target.value)} className="sr-only" />
                  </label>
                </div>
                {!ACCENT_COLORS.some(c => c.value === accentColor) && (
                  <p className="text-xs text-gray-500">Custom: {accentColor}</p>
                )}
              </div>
            ) : showProfile ? (
              <div className="p-4">
                <button onClick={() => setShowProfile(false)} className="flex items-center gap-2 text-gray-600 mb-4 hover:text-gray-800">
                  <ChevronRight size={16} className="rotate-180" /> Back
                </button>
                <h3 className="font-semibold mb-3">List Name</h3>
                <input
                  type="text"
                  value={titleText}
                  onChange={e => setTitleText(e.target.value)}
                  onBlur={() => saveListName(titleText)}
                  onKeyDown={e => { if (e.key === 'Enter') { saveListName(titleText); e.target.blur(); } }}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="List name"
                />
                <p className="text-xs text-gray-500 mt-2">Press Enter or click away to save</p>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">List ID</p>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">{listId}</code>
                </div>
              </div>
            ) : (
              <div className="py-2">
                <button
                  onClick={onNavigateHome}
                  className="flex items-center gap-2 p-3 w-full hover:bg-gray-100 rounded-lg mx-2 text-left"
                  style={{ width: 'calc(100% - 16px)' }}
                >
                  <ArrowLeft size={20} /><span>Home</span>
                </button>
                <button
                  onClick={() => { setShowSettings(true); }}
                  className="flex items-center gap-2 p-3 w-full hover:bg-gray-100 rounded-lg mx-2 text-left"
                  style={{ width: 'calc(100% - 16px)' }}
                >
                  <Settings size={20} /><span>Settings</span>
                </button>
                <button
                  onClick={() => { setShowProfile(true); }}
                  className="flex items-center gap-2 p-3 w-full hover:bg-gray-100 rounded-lg mx-2 text-left"
                  style={{ width: 'calc(100% - 16px)' }}
                >
                  <User size={20} /><span>Profile</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-start mb-4 gap-3">
            <button onClick={() => setIsMenuOpen(true)} className="p-2 hover:bg-gray-100 rounded mt-1">☰</button>
            <div className="flex-1 border-b-4" style={{ borderColor: accentColor }}>
              {editingTitle ? (
                <input
                  type="text"
                  value={titleText}
                  onChange={e => setTitleText(e.target.value)}
                  onBlur={() => { saveListName(titleText); setEditingTitle(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') { saveListName(titleText); setEditingTitle(false); } if (e.key === 'Escape') { setTitleText(list.name); setEditingTitle(false); } }}
                  className="text-3xl font-bold w-full outline-none border-b-2 mb-2"
                  style={{ borderColor: accentColor }}
                  autoFocus
                />
              ) : (
                <h1
                  className="text-3xl font-bold mb-1 cursor-pointer hover:opacity-80"
                  onDoubleClick={() => setEditingTitle(true)}
                  title="Double-click to edit"
                >
                  {list?.name || 'Loading…'}
                </h1>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-500 text-sm">{currentDate}</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{listId}</code>
                  <button
                    onClick={handleShare}
                    className="text-gray-400 hover:text-gray-700 flex items-center gap-1 text-xs"
                    title="Copy shareable link"
                  >
                    {copied ? <Check size={14} className="text-green-500" /> : <Share2 size={14} />}
                    {copied ? 'Copied!' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6 text-sm">
            {['active', 'completed', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`capitalize font-medium ${filter === f ? 'font-bold' : 'text-gray-600'}`}
                style={filter === f ? { color: accentColor } : {}}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Todo list */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="mb-6">
              <Droppable droppableId="todos">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                    {filteredTodos.map((todo, index) => (
                      <Draggable key={todo.id} draggableId={todo.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className="cursor-grab text-gray-400 flex-shrink-0"><GripVertical size={18} /></div>
                              <input
                                type="checkbox"
                                checked={todo.completed}
                                onChange={() => toggleTodo(todo.id)}
                                className="w-5 h-5 border-2 border-gray-300 rounded flex-shrink-0"
                              />
                              {editingTodoId === todo.id ? (
                                <input
                                  type="text"
                                  value={editingText}
                                  onChange={e => setEditingText(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveEdit(todo.id);
                                    if (e.key === 'Escape') { setEditingTodoId(null); setEditingText(''); }
                                  }}
                                  onBlur={() => saveEdit(todo.id)}
                                  className="flex-1 p-1 border border-gray-300 rounded min-w-0"
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className={`${todo.completed ? 'line-through text-gray-400' : ''} cursor-pointer truncate`}
                                  onDoubleClick={() => { setEditingTodoId(todo.id); setEditingText(todo.text); }}
                                  title="Double-click to edit"
                                >
                                  {todo.text}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 ml-2 flex-shrink-0">
                              <span className="text-xs text-gray-400 hidden sm:block">
                                {new Date(todo.time).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}
                              </span>
                              <button onClick={() => deleteTodo(todo.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {filteredTodos.length === 0 && (
                      <div className="text-center py-8 text-gray-400">
                        {filter === 'active' ? 'No active tasks — add one below!' :
                         filter === 'completed' ? 'No completed tasks yet.' :
                         'No tasks yet. Add one below!'}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>

          {/* Add todo */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={newTodo}
                onChange={e => setNewTodo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddTodo(); }}
                placeholder="Add a new task"
                className="flex-1 p-2 border border-gray-300 rounded"
              />
              <button
                onClick={handleAddTodo}
                className="text-white px-4 py-2 rounded flex items-center gap-2 hover:opacity-90"
                style={{ backgroundColor: accentColor }}
              >
                <Plus size={16} /> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoListPage;
